import { Check, FileDown, Trash2 } from "lucide-react";
import jsPDF from "jspdf";

export const STATUS_LABELS = {
    nouveau: "Nouveau",
    en_preparation: "En préparation",
    traite: "Traité",
};

const COLOR_MAP = {
    green: "text-green-700 hover:text-green-900 border-green-700",
    amber: "text-amber-700 hover:text-amber-900 border-amber-700",
    neutral: "text-[#5C3D2E] hover:text-[#2A1408] border-[#5C3D2E]",
};

function slugify(s) {
    return String(s || "message")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 60);
}

function downloadMessagePdf(m) {
    const status = m.status || (m.handled ? "traite" : "nouveau");
    const created = new Date(m.created_at).toLocaleString("fr-FR");
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 18;
    const maxW = pageW - margin * 2;
    let y = margin;

    // Header
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(176, 123, 102); // #B07B66
    doc.text("LE COMPTOIR DU COOKIE — MESSAGE RECU", margin, y);
    y += 7;

    doc.setTextColor(42, 20, 8); // #2A1408
    doc.setFontSize(18);
    const subjectLines = doc.splitTextToSize(m.subject || "Sans sujet", maxW);
    doc.text(subjectLines, margin, y);
    y += subjectLines.length * 7 + 2;

    // Underline
    doc.setDrawColor(42, 20, 8);
    doc.setLineWidth(0.6);
    doc.line(margin, y, pageW - margin, y);
    y += 6;

    // Meta
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(92, 61, 46); // #5C3D2E
    doc.text(`${created}  ·  Statut : ${STATUS_LABELS[status]}`, margin, y);
    y += 8;

    // Info rows
    const row = (label, value) => {
        if (!value) return;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(92, 61, 46);
        doc.text(label.toUpperCase(), margin, y);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(11);
        doc.setTextColor(42, 20, 8);
        const lines = doc.splitTextToSize(String(value), maxW - 40);
        doc.text(lines, margin + 40, y);
        y += Math.max(6, lines.length * 5.5);
    };
    row("Nom", m.name);
    row("Email", m.email);
    row("Téléphone", m.phone);
    row("Boutique", m.shop);

    // Body
    y += 4;
    doc.setDrawColor(228, 209, 187); // #E4D1BB
    doc.setLineDashPattern([1, 1], 0);
    doc.line(margin, y, pageW - margin, y);
    doc.setLineDashPattern([], 0);
    y += 8;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(42, 20, 8);
    const bodyLines = doc.splitTextToSize(m.message || "", maxW);
    for (const line of bodyLines) {
        if (y > pageH - margin) {
            doc.addPage();
            y = margin;
        }
        doc.text(line, margin, y);
        y += 5.5;
    }

    // Footer
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.setTextColor(176, 123, 102);
    doc.text(
        `Généré le ${new Date().toLocaleString("fr-FR")}`,
        pageW / 2,
        pageH - 10,
        { align: "center" }
    );

    const dateStr = new Date(m.created_at).toISOString().slice(0, 10);
    const fname = `message-${dateStr}-${slugify(m.name)}.pdf`;
    doc.save(fname);
}

/**
 * Reusable contact message card for both inbox and treated views.
 *
 * @param actions Array of { label, to (status), color }
 */
export default function ContactCard({ m, onSetStatus, onRemove, actions = [], muted = false }) {
    const status = m.status || (m.handled ? "traite" : "nouveau");
    return (
        <article
            className={`brand-card p-5 ${muted ? "opacity-70" : ""}`}
            data-testid={`contact-${m.id}`}
        >
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                    <p className="text-xs uppercase tracking-widest text-[#B07B66]">
                        {new Date(m.created_at).toLocaleString("fr-FR")} ·{" "}
                        {m.shop || "-"} · {STATUS_LABELS[status]}
                    </p>
                    <p className="font-serif-display text-lg text-[#2A1408] mt-1">
                        {m.subject || "Sans sujet"}
                    </p>
                    <p className="text-sm text-[#5C3D2E]">
                        {m.name} · {m.email} {m.phone ? `· ${m.phone}` : ""}
                    </p>
                    <p className="text-sm text-[#3D1F0F] mt-3 whitespace-pre-line break-words">
                        {m.message}
                    </p>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                    {actions.map((a) => (
                        <button
                            key={a.to}
                            type="button"
                            onClick={() => onSetStatus(m.id, a.to)}
                            className={`px-3 py-1.5 rounded-full border-2 text-xs font-bold whitespace-nowrap transition-colors ${COLOR_MAP[a.color] || COLOR_MAP.neutral}`}
                            data-testid={`btn-${a.to}-${m.id}`}
                        >
                            {a.color === "green" && <Check size={12} className="inline mr-1" />}
                            {a.label}
                        </button>
                    ))}
                    <button
                        type="button"
                        onClick={() => downloadMessagePdf(m)}
                        className="text-[#5C3D2E] hover:text-[#2A1408] mt-1 self-end"
                        aria-label="Télécharger en PDF"
                        title="Télécharger en PDF"
                        data-testid={`btn-pdf-${m.id}`}
                    >
                        <FileDown size={18} />
                    </button>
                    <button
                        type="button"
                        onClick={() => onRemove(m.id)}
                        className="text-red-700 hover:text-red-900 self-end"
                        aria-label="Supprimer"
                        data-testid={`btn-delete-${m.id}`}
                    >
                        <Trash2 size={18} />
                    </button>
                </div>
            </div>
        </article>
    );
}
